<template>
  <div v-if="announcements.length > 0">
    <!-- Collapsed state -->
    <div
      v-if="isCollapsed"
      class="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 cursor-pointer border border-gray-200 hover:bg-gray-100 transition-colors"
      @click="isCollapsed = false"
    >
      <span class="text-sm font-medium text-black">
        📢 Vous avez {{ announcements.length }} annonce{{ announcements.length > 1 ? 's' : '' }}
      </span>
      <svg class="h-4 w-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </div>

    <!-- Expanded state: carousel style -->
    <div v-else>
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-sm font-semibold text-black uppercase tracking-wide">Actualités</h3>
        <button
          class="text-xs text-black hover:text-black transition-colors"
          @click="isCollapsed = true"
        >
          Réduire ▲
        </button>
      </div>

      <!-- Carousel container -->
      <div class="relative">
        <!-- Slides viewport -->
        <div class="overflow-hidden rounded-lg">
          <div
            class="flex transition-transform duration-500 ease-in-out"
            :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
          >
            <div
              v-for="item in announcements"
              :key="item.id"
              class="w-full flex-shrink-0"
            >
              <div
                class="relative rounded-xl border border-gray-200 p-5 shadow-sm h-full"
                :style="{ backgroundColor: item.bgColor || '#ffffff' }"
              >
                <!-- Navigation arrows, top-right corner -->
                <div v-if="announcements.length > 1" class="absolute top-3 right-3 flex items-center gap-2">
                  <button
                    class="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 border border-gray-200 shadow-sm hover:bg-gray-200 hover:shadow transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    :disabled="currentIndex === 0"
                    @click="prev"
                  >
                    <svg class="h-3 w-3 text-black" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    class="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 border border-gray-200 shadow-sm hover:bg-gray-200 hover:shadow transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    :disabled="currentIndex === announcements.length - 1"
                    @click="next"
                  >
                    <svg class="h-3 w-3 text-black" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <div class="flex items-center gap-2 mb-2 pr-20">
                  <span class="text-lg">{{ typeIcon(item.type) }}</span>
                  <h4
                    class="font-semibold"
                    :style="{
                      color: item.titleColor || '#1a1a1a',
                      fontSize: item.titleFontSize || '15px',
                    }"
                  >
                    {{ item.title }}
                  </h4>
                </div>
                <div class="text-sm text-black leading-relaxed" v-html="item.content"></div>
                <span v-if="item.dateCreation" class="text-xs text-black mt-3 block">
                  {{ formatDate(item.dateCreation) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Dots -->
      <div v-if="announcements.length > 1" class="flex justify-center gap-2 mt-4">
        <button
          v-for="(_, idx) in announcements"
          :key="idx"
          class="w-2.5 h-2.5 rounded-full transition-all duration-300"
          :class="idx === currentIndex ? 'bg-gray-700 scale-110' : 'bg-gray-300 hover:bg-gray-400'"
          @click="currentIndex = idx"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { NewsItem } from '@/api/announcements'

const STORAGE_KEY = 'announcements_collapsed'

defineProps<{
  announcements: NewsItem[]
}>()

const isCollapsed = ref(localStorage.getItem(STORAGE_KEY) === 'true')
const currentIndex = ref(0)

watch(isCollapsed, (val) => {
  localStorage.setItem(STORAGE_KEY, String(val))
})

function prev() {
  if (currentIndex.value > 0) currentIndex.value--
}

function next() {
  if (currentIndex.value < 100) currentIndex.value++
}

function typeIcon(type: string | null) {
  switch (type) {
    case 'warning':
      return '⚠️'
    case 'important':
      return '🔴'
    default:
      return 'ℹ️'
  }
}

function formatDate(dateStr: string) {
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}
</script>
